import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    Box,
    Paper,
    Grid2,
    Alert,
    Button,
    CircularProgress,
    IconButton,
    Snackbar,
    Tooltip,
} from '@mui/material';
import HistoryBookIcon from '../components/icons/HistoryBookIcon';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { appBarActionButtonSx } from '../styles/msiAppBarStyles';

// Import your custom components
import SummaryTable from './../components/SummaryTable';
import RiskFactorsTable from './../components/risk_factor_table/RiskFactorsTable';
import AttributeRiskMetricsTableAndPlot from './../components/attribute_risk_metrics/AttributeRiskMetricsTableAndPlot';
import FactorExposureChart from './../components/FactorExposureChart';
import PositionDetailsTable from './../components/PositionDetailsTable';
import FloatingFactorPanel from '../components/risk_overview/FloatingFactorPanel';
import RiskWorkbookExportButton from '../components/risk_overview/RiskWorkbookExportButton';
import RiskOverviewInsights from '../components/risk_overview/RiskOverviewInsights';
import RiskOverviewAumControl from '../components/risk_overview/RiskOverviewAumControl';
import PortfolioGetStartedCallout from '../components/risk_overview/PortfolioGetStartedCallout';
import DashboardToolbarPortal from '../components/dashboard/components/DashboardToolbarPortal';
import {
    IDENTIFIER_EXAMPLES,
    PasteFromExcelDialog,
    usePagePaste,
} from '../components/shared/SheetPaste';
import { parseTsv } from '../components/watchlist_table/spreadsheet/clipboardTsv';
import { useImportFileTransfer } from '../context/ImportFileTransferContext';
import { isPastedSheet, pastedSheetFile } from '../components/watchlist_table/pastedSheet';
import { cardClasses } from '../styles/tailwindClasses';
import { riskOverviewPanelSx } from '../utils/riskOverviewStyles';
import { WORKSPACE_DIMENSIONS } from '../styles/workspaceChromeStyles';
import { RISK_OVERVIEW_RESOURCE_STATES } from '../components/dashboard/riskOverviewResource';
import { formatCoverageBannerText } from '../utils/positionReadiness';
import {
    POS_SIZING_RISK_SYNC_REQUIRED_CODE,
    POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE,
} from '../utils/posSizingRiskReadiness';
import {
    buildRiskFactorPresentation,
    buildAttributeRiskExportRows,
    buildSummaryPresentation,
} from '../utils/riskOverviewPresentation';
import { buildRiskOverviewPositionPresentation } from '../utils/riskOverviewPositionPresentation';
import { enrichFactorDetailRows } from '../utils/riskOverviewChartPresentation';
import { buildRiskOverviewWorkbookModel } from '../utils/riskOverviewWorkbook';
import {
    compactPanelPaddingSx,
} from '../components/pos_sizing_table/tableStyles';

const MIDDLE_ROW_CARD_HEIGHT = 423;
const PAGE_PASTE_HINT = 'Copy your sheet with a header row that includes '
    + `${IDENTIFIER_EXAMPLES} and Quantity to import your portfolio.`;
const EMPTY_ROWS = Object.freeze([]);
const EMPTY_METRICS = Object.freeze({});
const EXPORTABLE_RESOURCE_STATES = new Set([
    RISK_OVERVIEW_RESOURCE_STATES.PARTIAL,
    RISK_OVERVIEW_RESOURCE_STATES.READY,
]);

const isExportableResource = (resource) => (
    Boolean(resource?.data) && EXPORTABLE_RESOURCE_STATES.has(resource?.state)
);

const resourceSnapshotKey = (resource) => JSON.stringify([
    resource?.scopeKey ?? '',
    resource?.generation ?? 0,
    resource?.coverage?.snapshot_token ?? '',
]);

const resourcesShareSnapshot = (resourcesToCompare, { requireToken = false } = {}) => {
    const [firstResource] = resourcesToCompare;
    const scopesMatch = Boolean(firstResource?.scopeKey) && resourcesToCompare.every((resource) => (
        resource?.scopeKey === firstResource.scopeKey
        && resource?.generation === firstResource.generation
    ));
    const tokens = resourcesToCompare.map((resource) => resource?.coverage?.snapshot_token ?? '');
    const tokenCount = tokens.filter(Boolean).length;
    const tokensMatch = tokenCount === 0
        ? !requireToken
        : tokenCount === tokens.length && tokens.every((token) => token === tokens[0]);
    return scopesMatch && tokensMatch;
};

const middleLayerCardSx = {
    ...riskOverviewPanelSx,
    backgroundColor: 'background.paper',
    p: 1,
};

const RiskOverview = ({
    riskOverviewTopPanels,
    resources,
    selectedOptimalPortfolioName = '',
    selectedFactor,
    handleSelectFactor,
    sedolsForFactor,
    tickersLoading,
    tickersError,
    industries = EMPTY_ROWS,
    watchlistAttributeSnapshot = null,
    priceSyncLoading,
    priceSyncControl = null,
    onPriceSync = null,
    groupLoadingOverride = false,
    actualPortfolioEmpty = false,
    riskFactorsHeaderActions = null,
    aum = null,
    aumLoading = false,
    onOpenPortfolioImports = null,
    onOpenPortfolioEditor = null,
    onOpenPositionSizing = null,
    showImportGuide = false,
    onDismissImportGuide = null,
}) => {
    const middleRowRef = useRef(null);
    const [portfolioImportsButtonEl, setPortfolioImportsButtonEl] = useState(null);
    const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
    const [pasteHintOpen, setPasteHintOpen] = useState(false);
    const { transfer } = useImportFileTransfer();
    // A pasted sheet is a portfolio import, like an uploaded file: it opens
    // the portfolio imports with the same column check, review and Apply
    // (see watchlist_table/pastedSheet.js).
    const importPastedSheet = (pastedRows) => {
        if (!transfer || !isPastedSheet(pastedRows, 'portfolio')) return false;
        transfer('portfolio', pastedSheetFile(pastedRows));
        return true;
    };
    // A paste anywhere on the Risk Overview outside fields and dialogs.
    const riskOverviewPageRef = usePagePaste({
        enabled: Boolean(onOpenPortfolioImports),
        onPaste: (text) => {
            if (!importPastedSheet(parseTsv(text))) setPasteHintOpen(true);
        },
    });
    const [isFactorPanelOpen, setIsFactorPanelOpen] = useState(false);
    const [riskFactorSorting, setRiskFactorSorting] = useState([
        { id: 'percentVar', desc: true },
    ]);
    const [showAllModelFactors, setShowAllModelFactors] = useState(false);
    const actualResource = resources?.actual;
    const selectedResource = resources?.selected;
    const actualGroupResource = resources?.actualGroup;
    const selectedGroupResource = resources?.selectedGroup;
    const holdingsResource = resources?.holdings;
    const calculations = actualResource?.data ?? EMPTY_METRICS;
    const optimizedRiskMetrics = selectedResource?.data ?? EMPTY_METRICS;
    const groupRiskMetrics = actualGroupResource?.data ?? EMPTY_METRICS;
    const optimizedGroupRiskMetrics = selectedGroupResource?.data ?? EMPTY_METRICS;
    const combinedHoldings = holdingsResource?.data ?? EMPTY_ROWS;
    const factorDetailRows = useMemo(
        () => enrichFactorDetailRows(sedolsForFactor ?? [], combinedHoldings),
        [combinedHoldings, sedolsForFactor],
    );
    const actualRiskLoading = groupLoadingOverride
        || actualResource?.state === RISK_OVERVIEW_RESOURCE_STATES.LOADING;
    const optimizedRiskLoading = groupLoadingOverride
        || selectedResource?.state === RISK_OVERVIEW_RESOURCE_STATES.LOADING;
    // Actual-group metrics may load after the critical overview data. Their
    // table/chart has a local loading state, so that work must not re-block every
    // populated overview panel.
    const primaryRiskLoading = actualRiskLoading || optimizedRiskLoading;
    const hasCalculations = Boolean(
        actualResource?.data
        && (actualResource.data.totalRiskBps != null || actualResource.data.factorExposures?.length > 0)
    );
    const hasAttributeMetrics = Boolean(
        Array.isArray(groupRiskMetrics?.attribute_results)
        && groupRiskMetrics.attribute_results.length > 0
    );
    const isCalcInitialLoading = actualRiskLoading && !hasCalculations;
    const isAttributeInitialLoading = isCalcInitialLoading && !hasAttributeMetrics;
    const holdingsRefreshing = holdingsResource?.state
        === RISK_OVERVIEW_RESOURCE_STATES.LOADING;
    const tickersBusy = tickersLoading;
    const coverageText = formatCoverageBannerText(actualResource?.coverage);
    const resourceErrors = [
        actualResource?.error,
        selectedResource?.error,
        actualGroupResource?.error,
        selectedGroupResource?.error,
    ].filter(Boolean);
    const posSizingSyncRequired = resourceErrors.some(
        (error) => error.code === POS_SIZING_RISK_SYNC_REQUIRED_CODE,
    );
    const resourceErrorMessages = Array.from(new Set(resourceErrors
        .filter((error) => error.code !== POS_SIZING_RISK_SYNC_REQUIRED_CODE)
        .map((error) => error.message)
        .filter(Boolean)));
    const selectedPortfolioLoaded = Boolean(
        selectedResource?.data
        && selectedResource.state !== RISK_OVERVIEW_RESOURCE_STATES.LOADING,
    );
    const actualSnapshotKey = resourceSnapshotKey(actualResource);
    const selectedSnapshotKey = resourceSnapshotKey(selectedResource);
    const selectedPanelsComplete = Boolean(
        selectedResource?.state === RISK_OVERVIEW_RESOURCE_STATES.READY
        && selectedGroupResource?.state === RISK_OVERVIEW_RESOURCE_STATES.READY
        && resourcesShareSnapshot([selectedResource, selectedGroupResource]),
    );
    const summaryExportEnabled = isExportableResource(actualResource) && !actualPortfolioEmpty;
    const hasGroupedRiskRows = (groupRiskMetrics?.attribute_results ?? []).some(
        (result) => Array.isArray(result?.groups) && result.groups.length > 0,
    );
    const groupExportEnabled = isExportableResource(actualGroupResource)
        && hasGroupedRiskRows;
    const positionExportEnabled = isExportableResource(holdingsResource)
        && combinedHoldings.length > 0;
    const allExportEnabled = summaryExportEnabled
        && groupExportEnabled
        && positionExportEnabled
        && resourcesShareSnapshot(
            [actualResource, actualGroupResource, holdingsResource],
            { requireToken: true },
        );

    const summaryPresentation = useMemo(() => buildSummaryPresentation({
        calculations,
        selectedCalculations: optimizedRiskMetrics,
        selectedPortfolioLabel: selectedOptimalPortfolioName || 'Selected Portfolio',
        includeSelectedExport: selectedPanelsComplete,
    }), [
        calculations,
        optimizedRiskMetrics,
        selectedOptimalPortfolioName,
        selectedPanelsComplete,
    ]);
    const factorPresentation = useMemo(() => buildRiskFactorPresentation({
        calculations,
        selectedCalculations: optimizedRiskMetrics,
        emptyPortfolio: actualPortfolioEmpty,
        sorting: riskFactorSorting,
        includeSelectedExport: selectedPanelsComplete,
        showAllModelFactors,
    }), [
        actualPortfolioEmpty,
        calculations,
        optimizedRiskMetrics,
        riskFactorSorting,
        selectedPanelsComplete,
        showAllModelFactors,
    ]);
    const attributeExportRows = useMemo(() => buildAttributeRiskExportRows(
        groupRiskMetrics,
        optimizedGroupRiskMetrics,
        { includeSelectedExport: selectedPanelsComplete },
    ), [optimizedGroupRiskMetrics, groupRiskMetrics, selectedPanelsComplete]);
    const positionPresentation = useMemo(() => buildRiskOverviewPositionPresentation({
        holdings: combinedHoldings,
        optimizedHoldings: optimizedRiskMetrics?.holdingsCalculations ?? EMPTY_ROWS,
        industries,
        watchlistAttributeSnapshot,
        includeSelectedExport: selectedPanelsComplete,
        selectedHoldingsLoaded: selectedPortfolioLoaded,
    }), [
        combinedHoldings,
        industries,
        optimizedRiskMetrics?.holdingsCalculations,
        selectedPanelsComplete,
        selectedPortfolioLoaded,
        watchlistAttributeSnapshot,
    ]);
    const workbookModel = useMemo(() => buildRiskOverviewWorkbookModel({
        summaryRows: summaryPresentation.exportRows,
        factorSummaryRows: factorPresentation.summaryExportRows,
        factorGroupRows: factorPresentation.groupedExportRows,
        groupRows: attributeExportRows,
        positionRows: positionPresentation.exportRows,
        groupingSheetName: 'by_attribute',
    }), [
        attributeExportRows,
        factorPresentation,
        positionPresentation,
        summaryPresentation,
    ]);

    const handleRiskFactorSelect = useCallback((factorName) => {
        setIsFactorPanelOpen(Boolean(factorName));
        handleSelectFactor(factorName);
    }, [handleSelectFactor]);

    const handleRiskFactorSortingChange = useCallback((nextSorting) => {
        setRiskFactorSorting((nextSorting || []).filter((item) => item?.id).slice(0, 1));
    }, []);

    const handleCloseFactorPanel = useCallback(() => {
        setIsFactorPanelOpen(false);
        handleSelectFactor('');
    }, [handleSelectFactor]);

    useEffect(() => {
        if (!selectedFactor) {
            setIsFactorPanelOpen(false);
        }
    }, [selectedFactor]);

    useEffect(() => {
        setShowAllModelFactors(false);
    }, [actualSnapshotKey, selectedSnapshotKey]);

    return (
        <>
            <DashboardToolbarPortal>
                {riskFactorsHeaderActions}
                <RiskOverviewAumControl aum={aum} loading={aumLoading} />
                {priceSyncControl?.visible && (
                    <Tooltip title={priceSyncControl.tooltip || ''} arrow>
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onPriceSync}
                                disabled={priceSyncControl.disabled || !onPriceSync}
                                startIcon={priceSyncControl.loading
                                    ? <CircularProgress size={13} thickness={5} color="inherit" />
                                    : <SyncRoundedIcon sx={{ fontSize: 17 }} />}
                                sx={{
                                    height: 32,
                                    minWidth: 0,
                                    px: 1.2,
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    color: 'text.secondary',
                                    backgroundColor: 'background.paper',
                                    textTransform: 'none',
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        borderColor: 'divider',
                                        backgroundColor: 'action.hover',
                                        color: 'text.primary',
                                    },
                                }}
                            >
                                {priceSyncControl.label || 'Sync prices'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {onOpenPortfolioImports && (
                    <Tooltip title="Portfolio imports and history">
                      <IconButton
                        ref={setPortfolioImportsButtonEl}
                        size="small"
                        onClick={onOpenPortfolioImports}
                        aria-label="Open portfolio imports"
                        sx={appBarActionButtonSx}
                      >
                        <HistoryBookIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                )}
                <RiskWorkbookExportButton
                    tooltip="Download all risk data"
                    ariaLabel="Download all risk data"
                    fileName={workbookModel.fileName}
                    sheets={workbookModel.sheets}
                    sx={appBarActionButtonSx}
                    disabled={!allExportEnabled}
                />
            </DashboardToolbarPortal>
            {onOpenPortfolioImports && (
                <PortfolioGetStartedCallout
                    open={Boolean(showImportGuide)}
                    anchorEl={portfolioImportsButtonEl}
                    onUpload={onOpenPortfolioImports}
                    onPaste={() => setPasteDialogOpen(true)}
                    onPickFromWatchlist={onOpenPortfolioEditor}
                    onDismiss={onDismissImportGuide}
                />
            )}
            <PasteFromExcelDialog
                open={pasteDialogOpen}
                destination="portfolio"
                onImport={importPastedSheet}
                onClose={() => setPasteDialogOpen(false)}
            />
            <Snackbar open={pasteHintOpen} autoHideDuration={5000} onClose={() => setPasteHintOpen(false)}>
                <Alert severity="info" onClose={() => setPasteHintOpen(false)} sx={{ width: '100%' }}>
                    {PAGE_PASTE_HINT}
                </Alert>
            </Snackbar>
            {(riskOverviewTopPanels || []).length > 0 && (
                <Grid2
                    container
                    spacing={1.5}
                    mb={2}
                    alignItems="flex-start"
                    sx={{ mt: { xs: '-14px', md: '-22px' } }}
                >
                    {(riskOverviewTopPanels || []).map(({ colWidth, element }, idx) => (
                        <Grid2
                            size={{ md: colWidth, xs: 12 }}
                            key={idx}
                            sx={colWidth === 12 ? { width: '100%' } : { flexGrow: 0, width: 'auto' }}
                        >
                            {element}
                        </Grid2>
                    ))}
                </Grid2>
            )}
            <Box ref={riskOverviewPageRef}>
                {posSizingSyncRequired && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={onOpenPositionSizing && (
                            <Button color="inherit" size="small" onClick={onOpenPositionSizing}>
                                Open Position Sizing
                            </Button>
                        )}
                    >
                        {POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE}
                    </Alert>
                )}
                {resourceErrorMessages.map((message) => (
                    <Alert key={message} severity="error" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                ))}
                {coverageText && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {coverageText}
                    </Alert>
                )}
                <Box
                    ref={middleRowRef}
                    sx={{ position: 'relative', mb: `${WORKSPACE_DIMENSIONS.sectionGap}px` }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                lg: 'minmax(300px, 0.62fr) minmax(560px, 1.75fr)',
                                xl: 'minmax(300px, 0.62fr) minmax(560px, 1.75fr) minmax(300px, 0.72fr)',
                            },
                            gap: `${WORKSPACE_DIMENSIONS.sectionGap}px`,
                            alignItems: 'stretch',
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: 0,
                                display: 'flex',
                                overflow: 'hidden',
                            }}
                        >
                            <Paper
                                className={cardClasses}
                                elevation={0}
                                sx={{
                                    ...middleLayerCardSx,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    minHeight: MIDDLE_ROW_CARD_HEIGHT,
                                    maxHeight: MIDDLE_ROW_CARD_HEIGHT,
                                    boxSizing: 'border-box',
                                }}
                            >
                                <SummaryTable
                                    presentation={summaryPresentation}
                                    directExportEnabled={summaryExportEnabled}
                                    isLoading={actualRiskLoading}
                                    optimizedLoading={optimizedRiskLoading}
                                />
                            </Paper>
                        </Box>

                        <Box
                            sx={{
                                minWidth: 0,
                                display: 'flex',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Paper
                                className={cardClasses}
                                elevation={0}
                                sx={{
                                    ...middleLayerCardSx,
                                    flex: 1,
                                    overflow: 'hidden',
                                    minHeight: MIDDLE_ROW_CARD_HEIGHT,
                                    maxHeight: MIDDLE_ROW_CARD_HEIGHT,
                                    boxSizing: 'border-box',
                                }}
                            >
                                <RiskFactorsTable
                                    presentation={factorPresentation}
                                    selectedFactor={selectedFactor}
                                    onSelectFactor={handleRiskFactorSelect}
                                    sorting={riskFactorSorting}
                                    onSortingChange={handleRiskFactorSortingChange}
                                    directExportEnabled={summaryExportEnabled}
                                    showAllModelFactors={showAllModelFactors}
                                    onToggleShowAllModelFactors={() => (
                                        setShowAllModelFactors((current) => !current)
                                    )}
                                    canShowAllModelFactors={Boolean(
                                        calculations?.modelFactorCatalog?.length
                                        || optimizedRiskMetrics
                                            ?.modelFactorCatalog?.length
                                    )}
                                    isLoading={actualRiskLoading}
                                    optimizedLoading={optimizedRiskLoading}
                                />
                            </Paper>
                        </Box>

                        <Box
                            sx={{
                                minWidth: 0,
                                height: MIDDLE_ROW_CARD_HEIGHT,
                                gridColumn: { lg: '1 / -1', xl: 'auto' },
                            }}
                        >
                            <RiskOverviewInsights
                                calculations={calculations}
                                optimizedCalculations={optimizedRiskMetrics}
                                showOptimized={selectedPortfolioLoaded}
                                isLoading={primaryRiskLoading}
                                emptyPortfolio={actualPortfolioEmpty}
                                aum={aum}
                            />
                        </Box>

                    </Box>
                </Box>
                <FloatingFactorPanel
                    open={Boolean(selectedFactor) && isFactorPanelOpen}
                    factorName={selectedFactor}
                    tickers={factorDetailRows}
                    isLoading={tickersBusy}
                    error={tickersError}
                    containerRef={middleRowRef}
                    snapshotKey={actualSnapshotKey}
                    onClose={handleCloseFactorPanel}
                />

                <Grid2 size={{ md: 12 }} mb={`${WORKSPACE_DIMENSIONS.sectionGap}px`}>
                    <Paper
                        className={cardClasses}
                        elevation={0}
                        sx={{ ...riskOverviewPanelSx, ...compactPanelPaddingSx }}
                    >
                        <AttributeRiskMetricsTableAndPlot
                            actualMetrics={groupRiskMetrics}
                            optimizedMetrics={optimizedGroupRiskMetrics}
                            actualLoading={
                                groupLoadingOverride
                                || actualGroupResource?.state
                                    === RISK_OVERVIEW_RESOURCE_STATES.LOADING
                                || isAttributeInitialLoading
                            }
                            optimizedLoading={
                                groupLoadingOverride
                                || selectedGroupResource?.state
                                    === RISK_OVERVIEW_RESOURCE_STATES.LOADING
                                || isAttributeInitialLoading
                            }
                            optimalPortfolioLoaded={selectedPortfolioLoaded}
                            directExportEnabled={groupExportEnabled}
                            includeSelectedExport={selectedPanelsComplete}
                        />
                    </Paper>
                </Grid2>

                <Grid2 size={{ md: 12 }} mb={`${WORKSPACE_DIMENSIONS.sectionGap}px`}>
                    <FactorExposureChart
                        calculations={calculations}
                        optimizedCalculations={optimizedRiskMetrics}
                        emptyPortfolio={actualPortfolioEmpty}
                        isLoading={primaryRiskLoading}
                    />
                </Grid2>

                <Grid2 size={{ md: 12 }} mb={`${WORKSPACE_DIMENSIONS.sectionGap}px`}>
                    <Paper
                        className={cardClasses}
                        elevation={0}
                        sx={{ ...riskOverviewPanelSx, ...compactPanelPaddingSx }}
                    >
                        <PositionDetailsTable
                            holdings={combinedHoldings}
                            positionPresentation={positionPresentation}
                            marketDataRows={industries}
                            priceSyncLoading={priceSyncLoading}
                            directExportEnabled={positionExportEnabled}
                            isLoading={holdingsRefreshing}
                            optimizedLoading={optimizedRiskLoading}
                            onOpenPortfolioEditor={onOpenPortfolioEditor}
                        />
                    </Paper>
                </Grid2>
            </Box>
        </>
    );
};

export default RiskOverview;
